import React, { createElement, memo, useEffect } from 'react';
import { TComponentStyling } from '@ldp/common-types';
import { WrapperComponent } from '../wrapper';
import { ErrorBoundary } from '@ldp/error-boundary';
import { NavTilesSkeleton } from '@ldp-web/component-library/ui/skeletons/nav-tiles-skeleton';

export interface IFireworkProps {
  fireworkHTML: string;
  styling?: TComponentStyling;
  isFullWidth?: boolean;
  id?: string;
}

interface FireworkNavigationEvent extends CustomEvent {
  detail: {
    url?: string;
  };
}

/** renders Firework Widget*/
const Firework = (props: IFireworkProps) => {
  const IS_SSR = typeof window === 'undefined';

  const { fireworkHTML, styling, isFullWidth, id } = props;

  /** parses out script tag, returns only data required to render widget */
  const widgetData = fireworkHTML.split('</script>')[1];

  /** data copied from Firework CMS , channel is always first attribute */
  const widgetTag = widgetData.split('channel')[0].split('<')[1].trim();

  /** organize attributes to pass as an object to create Widget  */
  const widgetAttributes: any = {};
  widgetData
    /** isolates just the attribute data */
    .split(widgetTag)[1]
    .split('>')[0]
    .split('\n')
    .map((attribute) => {
      return attribute.trim();
    })
    /** filter out any empty key:value pair */
    .filter((attribute) => attribute.length > 1)
    /** creates an array of objects, each object contains the attribute key mapped to attribute value */
    .map((attribute) => {
      const propKey = attribute.split('=')[0];
      const propValue = attribute.split('=')[1];
      return { [propKey]: propValue };
    })
    /**create the widgetAttribute object */
    .forEach((attributeObject) => {
      const propsKey = Object.keys(attributeObject)[0];
      const propsValue = attributeObject[propsKey];
      widgetAttributes[propsKey] = propsValue.replace(/"/g, '');
    });

  const FireworkWidget = () => {
    return createElement(widgetTag, { ...widgetAttributes });
  };

  const playerNavigationHandler = (event: FireworkNavigationEvent) => {
    if (event?.detail?.url && !IS_SSR) {
      window.location.assign(event.detail.url);
    }
  };

  useEffect(() => {
    if (!IS_SSR && window._fwn && fireworkHTML) {
      /** event listeners for navigation events */
      document.addEventListener('fw:player:navigate', playerNavigationHandler as EventListener);
      document.addEventListener('fw:navigate', playerNavigationHandler as EventListener);
    }
    return () => {
      if (!IS_SSR && window._fwn && fireworkHTML) {
        document.removeEventListener('fw:player:navigate', playerNavigationHandler as EventListener);
        document.removeEventListener('fw:navigate', playerNavigationHandler as EventListener);
      }
    };
  }, [fireworkHTML]);

  if (!IS_SSR && window._fwn && fireworkHTML) {
    return (
      <ErrorBoundary FallbackComponent={() => <NavTilesSkeleton />}>
        <WrapperComponent data-testid="firework-component" styling={styling} isNoPadding={isFullWidth} id={id}>
          <FireworkWidget />
        </WrapperComponent>
      </ErrorBoundary>
    );
  } else return null;
};

export const FireworkComponent = memo(Firework);
